import { authorName } from '@/lib/note-format';
import { isThreadResolved } from '@/lib/notes-queries';
import {
  type Dimension,
  NOTE_VISIBILITIES,
  type NoteTarget,
  type NoteThread,
  PALIER_NUMBERS,
  type SingularityLevier,
} from '@mss_creator/shared';
import type { NotesTargetType, NotesViewSettings } from './notes-view-search';

/**
 * What the Notes view does with the discussions of a matrix (issue #19,
 * `docs/specs/matrices.md` « Vue Notes »): filter them, sort them, count them.
 * Pure, so each rule is asserted on its own.
 */

/** Lower-cased and stripped of accents: « Réponse » and « reponse » meet. */
export function normalizeText(text: string): string {
  return text.normalize('NFD').replace(/\p{M}/gu, '').toLowerCase();
}

function threadMatchesQuery(thread: NoteThread, normalizedQuery: string): boolean {
  if (normalizeText(thread.body).includes(normalizedQuery)) return true;
  return thread.replies.some((reply) => normalizeText(reply.body).includes(normalizedQuery));
}

/** The type the « Cible » filter tells apart (a folder note never reaches a matrix view). */
export function notesTargetType(target: NoteTarget): NotesTargetType {
  return target.type === 'folder' ? 'matrix' : target.type;
}

/**
 * The discussions the settings let through: status, level, author, type of
 * target and text (in the note and in its replies). The order is untouched.
 */
export function filterThreads(threads: NoteThread[], settings: NotesViewSettings): NoteThread[] {
  const query = normalizeText(settings.q.trim());
  return threads.filter((thread) => {
    if (settings.status === 'ouvertes' && isThreadResolved(thread)) return false;
    if (settings.status === 'resolues' && !isThreadResolved(thread)) return false;
    if (settings.levels.length > 0 && !settings.levels.includes(thread.visibility)) return false;
    if (settings.authors.length > 0 && !settings.authors.includes(thread.author?.id ?? ''))
      return false;
    if (
      settings.targetTypes.length > 0 &&
      !settings.targetTypes.includes(notesTargetType(thread.target))
    )
      return false;
    return query === '' || threadMatchesQuery(thread, query);
  });
}

/** Whether any filter narrows the list (the status and the sort do not count). */
export function hasActiveFilters(settings: NotesViewSettings): boolean {
  return (
    settings.levels.length > 0 ||
    settings.authors.length > 0 ||
    settings.targetTypes.length > 0 ||
    settings.q.trim() !== ''
  );
}

/** « 5 discussions ouvertes · 1 résolue », from every discussion of the matrix. */
export function statusSummary(threads: NoteThread[]): string {
  const resolved = threads.filter(isThreadResolved).length;
  const open = threads.length - resolved;
  const openPart = open === 1 ? '1 discussion ouverte' : `${open} discussions ouvertes`;
  if (resolved === 0) return openPart;
  return `${openPart} · ${resolved === 1 ? '1 résolue' : `${resolved} résolues`}`;
}

/** The authors of the discussions, one entry each, alphabetically: the « Auteur » filter's options. */
export function authorOptions(threads: NoteThread[]): { id: string; name: string }[] {
  const byId = new Map<string, string>();
  for (const thread of threads) {
    if (thread.author) byId.set(thread.author.id, thread.author.displayName);
  }
  return [...byId]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name, 'fr'));
}

// ── Sorting ──────────────────────────────────────────────────────────────────

/** Where each column and row stands in the grid: what « Ordre de la grille » sorts on. */
export interface GridOrder {
  columns: ReadonlyMap<number, number>;
  rows: ReadonlyMap<string, number>;
}

/** The order of the grid: the paliers left to right, the six activation rows, then the dimensions. */
export function buildGridOrder(
  singularitySetup: SingularityLevier[],
  dimensions: Dimension[],
): GridOrder {
  const columns = new Map<number, number>(PALIER_NUMBERS.map((number, index) => [number, index]));
  const rows = new Map<string, number>();
  const activations = [...singularitySetup].sort((a, b) => a.order - b.order);
  for (const levier of activations) rows.set(`activation:${levier.order}`, rows.size);
  const ordered = [...dimensions].sort((a, b) => a.displayOrder - b.displayOrder);
  for (const dimension of ordered) rows.set(`dimension:${dimension.id}`, rows.size);
  return { columns, rows };
}

const LAST = Number.MAX_SAFE_INTEGER;

/**
 * The position of a spot in the grid as a tuple: the matrix first, then the
 * columns, then the rows, then the cells line by line.
 */
function gridPosition(target: NoteTarget, order: GridOrder): [number, number, number] {
  const column = (number: number) => order.columns.get(number) ?? LAST;
  const row = (rowType: string, rowRef: string) => order.rows.get(`${rowType}:${rowRef}`) ?? LAST;
  switch (target.type) {
    case 'matrix':
    case 'folder':
      return [0, 0, 0];
    case 'column':
      return [1, column(target.columnNumber), 0];
    case 'row':
      return [2, row(target.rowType, target.rowRef), 0];
    case 'cell':
      return [3, row(target.rowType, target.rowRef), column(target.columnNumber)];
  }
}

function compareTuples(a: readonly number[], b: readonly number[]): number {
  for (let index = 0; index < a.length; index += 1) {
    const difference = (a[index] ?? 0) - (b[index] ?? 0);
    if (difference !== 0) return difference;
  }
  return 0;
}

function compareByCriterion(
  a: NoteThread,
  b: NoteThread,
  sort: NotesViewSettings['sort'],
  order: GridOrder,
): number {
  switch (sort) {
    case 'recentes':
      return a.createdAt.localeCompare(b.createdAt);
    case 'activite':
      return a.lastActivityAt.localeCompare(b.lastActivityAt);
    case 'grille':
      return compareTuples(gridPosition(a.target, order), gridPosition(b.target, order));
    case 'auteur':
      return authorName(a.author).localeCompare(authorName(b.author), 'fr');
    case 'niveau':
      return NOTE_VISIBILITIES.indexOf(a.visibility) - NOTE_VISIBILITIES.indexOf(b.visibility);
  }
}

/**
 * A sorted copy. The direction applies to the criterion; discussions that tie
 * on it keep the newest first, so a list never reshuffles between renders.
 */
export function sortThreads(
  threads: NoteThread[],
  settings: Pick<NotesViewSettings, 'sort' | 'dir'>,
  order: GridOrder,
): NoteThread[] {
  const sign = settings.dir === 'asc' ? 1 : -1;
  return [...threads].sort((a, b) => {
    const primary = compareByCriterion(a, b, settings.sort, order);
    if (primary !== 0) return sign * primary;
    return b.createdAt.localeCompare(a.createdAt) || a.id.localeCompare(b.id);
  });
}

/**
 * The spot of a discussion split the way the cards and the table show it: a
 * small word for its kind (« Case », « Ligne », « Palier », « Matrice ») and
 * the label proper (« Suivi après la visite × Barre des minimas »).
 */
export function targetTypeWord(thread: Pick<NoteThread, 'target' | 'targetLabel'>): {
  typeWord: string;
  label: string;
} {
  switch (thread.target.type) {
    case 'cell':
      return { typeWord: 'Case', label: thread.targetLabel };
    case 'row':
      return { typeWord: 'Ligne', label: thread.targetLabel.replace(/^Ligne /, '') };
    case 'column':
      return { typeWord: 'Palier', label: thread.targetLabel.replace(/^Palier /, '') };
    case 'folder':
      return { typeWord: 'Dossier', label: thread.targetLabel.replace(/^Dossier /, '') };
    case 'matrix':
      return { typeWord: 'Matrice', label: thread.targetLabel };
  }
}
